// src/context/AuthContext.js
import { createContext, useState, useEffect, useContext, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { baseURL } from "../utils/config";
import { getDashboardRoute } from "../utils/authHelpers";

const Swal = require("sweetalert2");

const AuthContext = createContext();

export default AuthContext;

export const useAuth = () => useContext(AuthContext);

// Retourne true si le token est expiré ou illisible
const isExpired = (token) => {
  try {
    return jwtDecode(token).exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const [authTokens, setAuthTokens] = useState(() => {
    try {
      const saved = localStorage.getItem("authTokens");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // L'utilisateur est défini après validation de la session (voir useEffect)
  const [user, setUser] = useState(null);

  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // Évite le double exécution en React StrictMode (dev), qui lancerait
  // deux refresh avec le même refresh token (le 2e échouerait : blacklist)
  const initDone = useRef(false);

  // ─── LOGIN ────────────────────────────────────────────────────────────────
  const loginUser = async (username, password) => {
    try {
      const response = await fetch(`${baseURL}/token/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok) {
        const decodedUser = jwtDecode(data.access);

        setAuthTokens(data);
        setUser(decodedUser);

        localStorage.setItem("authTokens", JSON.stringify(data));

        navigate(
          getDashboardRoute(decodedUser.role, decodedUser.etablissement_slug)
        );

        Swal.fire({
          title: "Connexion réussie",
          icon: "success",
          toast: true,
          timer: 2000,
          position: "top-right",
          timerProgressBar: true,
          showConfirmButton: false,
        });
      } else {
        if (response.status === 503 && data.error) {
          Swal.fire({
            title: "🚧 Maintenance",
            text: data.error || "Le site est actuellement en maintenance.",
            icon: "info",
            confirmButtonText: "Ok",
            allowOutsideClick: false,
          });
        } else {
          throw new Error(data?.detail || "Identifiants incorrects");
        }
      }
    } catch (error) {
      if (error?.response?.status !== 503) {
        Swal.fire({
          title: "Identifiants incorrects",
          icon: "error",
          toast: true,
          timer: 2500,
          position: "top-right",
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
      throw error;
    }
  };

  // ─── REGISTER ─────────────────────────────────────────────────────────────
  const registerUser = async (userData) => {
    try {
      const response = await fetch(`${baseURL}/register/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (response.status === 201) {
        Swal.fire({
          title: "Inscription réussie ! 🎉",
          text: "Vous pouvez maintenant vous connecter.",
          icon: "success",
          timer: 3000,
          position: "top-right",
          timerProgressBar: true,
          showConfirmButton: false,
        });

        // Rediriger vers la page de connexion
        navigate("/login");
        return data;
      } else {
        // Gérer les erreurs du backend
        let errorMessage = "Erreur lors de l'inscription. Veuillez réessayer.";

        if (data) {
          if (typeof data === "object") {
            // Construire un message d'erreur à partir des champs
            const errors = [];
            if (data.username) errors.push(data.username.join(", "));
            if (data.email) errors.push(data.email.join(", "));
            if (data.password) errors.push(data.password.join(", "));
            if (data.first_name) errors.push(data.first_name.join(", "));
            if (data.last_name) errors.push(data.last_name.join(", "));
            if (data.telephone1) errors.push(data.telephone1.join(", "));
            if (data.telephone2) errors.push(data.telephone2.join(", "));
            if (data.detail) errors.push(data.detail);
            if (data.error) errors.push(data.error);

            if (errors.length > 0) {
              errorMessage = errors.join("\n");
            }
          } else if (typeof data === "string") {
            errorMessage = data;
          }
        }

        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Erreur d'inscription:", error);

      Swal.fire({
        title: "Erreur d'inscription",
        text:
          error.message || "Erreur lors de l'inscription. Veuillez réessayer.",
        icon: "error",
        timer: 3000,
        position: "top-right",
        timerProgressBar: true,
        showConfirmButton: false,
      });

      throw error;
    }
  };

  // ─── LOGOUT ──────────────────────────────────────────────────────────────
  const logoutUser = (silent = false) => {
    setAuthTokens(null);
    setUser(null);

    localStorage.removeItem("authTokens");

    navigate("/login");

    if (!silent) {
      Swal.fire({
        title: "Déconnexion",
        icon: "info",
        toast: true,
        timer: 2000,
        position: "top-right",
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  // ─── VALIDATION DE LA SESSION AU CHARGEMENT DE L'APP ─────────────────────
  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;

    const clearSession = () => {
      setAuthTokens(null);
      setUser(null);
      localStorage.removeItem("authTokens");
    };

    const initSession = async () => {
      // Pas de tokens : visiteur non connecté
      if (!authTokens) {
        setLoading(false);
        return;
      }

      // Access encore valide : session OK
      if (!isExpired(authTokens.access)) {
        setUser(jwtDecode(authTokens.access));
        setLoading(false);
        return;
      }

      // Access expiré ET refresh expiré : vraie déconnexion
      if (isExpired(authTokens.refresh)) {
        clearSession();
        setLoading(false);
        return;
      }

      // Access expiré mais refresh valide : on renouvelle
      try {
        const response = await fetch(`${baseURL}/token/refresh/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh: authTokens.refresh }),
        });

        if (!response.ok) throw new Error("Refresh refusé");

        // ROTATE_REFRESH_TOKENS=True : la réponse contient access + nouveau refresh
        const data = await response.json();
        localStorage.setItem("authTokens", JSON.stringify(data));
        setAuthTokens(data);
        setUser(jwtDecode(data.access));
      } catch {
        clearSession();
      }

      setLoading(false);
    };

    initSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // une seule fois au montage

  const contextData = {
    user,
    setUser,
    authTokens,
    setAuthTokens,
    loginUser,
    registerUser,
    logoutUser,
    etablissementSlug: user?.etablissement_slug ?? null,
    isAuthenticated: !!user,
    loading,
  };

  return (
    <AuthContext.Provider value={contextData}>
      {loading ? null : children}
    </AuthContext.Provider>
  );
};